/**
 * ReportButton — reusable report-content trigger + dialog.
 *
 * Usage:
 *   <ReportButton contentType="catch" contentId={catch_.id} />
 *   <ReportButton contentType="submission" contentId={submission.id} variant="icon" />
 *
 * Posts to POST /api/reports. Duplicate reports from the same user are silently
 * deduped server-side, so the button is safe to spam-click.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { id: 'spam',        label: 'Spam' },
  { id: 'abuse',       label: 'Abuse or harassment' },
  { id: 'fake_catch',  label: 'Fake catch' },
  { id: 'offensive',   label: 'Offensive content' },
  { id: 'other',       label: 'Other' },
];

export default function ReportButton({ contentType, contentId, variant = 'text', className = '' }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem('carpcast_token');

  const handleSubmit = async () => {
    if (!token) {
      toast.error('Please sign in to report content');
      return;
    }
    if (!reason) {
      toast.error('Please choose a reason');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/reports`,
        {
          content_type: contentType,
          content_id: contentId,
          reason,
          description: description.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Thanks — our team will review this.', { duration: 5000 });
      setOpen(false);
      setReason('');
      setDescription('');
    } catch (err) {
      const d = err.response?.data?.detail || 'Could not submit report. Try again later.';
      toast.error(d, { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {variant === 'icon' ? (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className={`text-[#8a948a]/70 hover:text-red-400 transition-colors p-1 ${className}`}
          aria-label="Report content"
          data-testid={`report-btn-${contentType}-${contentId}`}
        >
          <Flag className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className={`text-xs text-[#8a948a]/70 hover:text-red-400 transition-colors inline-flex items-center gap-1 ${className}`}
          data-testid={`report-btn-${contentType}-${contentId}`}
        >
          <Flag className="w-3 h-3" />
          Report
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141714] border-[#1f221f] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-wider">Report this {contentType}</DialogTitle>
            <DialogDescription className="text-[#8a948a]">
              Reports are private. Our team will review and act on anything that breaks Carp Cast rules.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#8a948a] mb-2">Reason</p>
              <div className="space-y-1.5">
                {REASONS.map((r) => (
                  <label
                    key={r.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                      reason === r.id
                        ? 'border-[#c8b273] bg-[#c8b273]/10'
                        : 'border-[#1f221f] hover:border-[#c8b273]/40'
                    }`}
                    data-testid={`report-reason-${r.id}`}
                  >
                    <input
                      type="radio"
                      name="report-reason"
                      value={r.id}
                      checked={reason === r.id}
                      onChange={(e) => setReason(e.target.value)}
                      className="accent-[#c8b273]"
                    />
                    <span className="text-sm">{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-[#8a948a] mb-2">Add details (optional)</p>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Anything else we should know?"
                className="bg-[#0b0d0b] border-[#1f221f]"
                data-testid="report-description"
              />
              <p className="text-[10px] text-[#6b7d6b] mt-1 text-right">{description.length}/500</p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="report-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !reason}
                className="btn-gold"
                data-testid="report-submit-btn"
              >
                {submitting ? 'Submitting...' : 'Submit report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
